import { MyNavigator } from 'typings/globals/cordova';
import {
  PermissionStateEnum,
  keepLocationPermission,
  polyfill,
} from 'utils/permissions.request';

import { LocationPermissions, PermissionProvider, Permissions } from './types';

declare const navigator: MyNavigator;

polyfill();

type PermissionListener = (permissions: Permissions) => void;

export class BrowserPermissionProvider implements PermissionProvider {
  private listeners = new Set<PermissionListener>();
  private lastPermissions: Permissions | null = null;

  constructor() {
    this.refreshPermissions();
  }

  subscribe(listener: PermissionListener): () => void {
    this.listeners.add(listener);
    if (this.lastPermissions) {
      listener(this.lastPermissions);
    }
    return () => this.listeners.delete(listener);
  }

  private notify(permissions: Permissions): void {
    this.lastPermissions = permissions;
    this.listeners.forEach((l) => l(permissions));
  }

  async refreshPermissions(): Promise<Permissions> {
    const [location, push, camera] = await Promise.all([
      this.hasLocationPermission(),
      this.hasPush(),
      this.hasCamera(),
    ]);

    const permissions: Permissions = {
      location,
      push,
      camera,
      backgroundRefresh: true,
    };

    this.notify(permissions);
    return permissions;
  }

  async requestLocationBackgroundPermission(): Promise<boolean> {
    return this.requestLocationInUsePermission();
  }

  async askForLocationPermissionsAfterDenial(): Promise<boolean> {
    return this.requestLocationInUsePermission();
  }

  async requestLocationInUsePermission(): Promise<boolean> {
    if (!navigator.geolocation.getCurrentPosition) {
      return false;
    }

    try {
      await new Promise<void>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(() => resolve(), reject, {}),
      );
      keepLocationPermission();
      setTimeout(() => this.refreshPermissions(), 200);
      const { inUse } = await this.hasLocationPermission();
      return inUse;
    } catch (e) {
      keepLocationPermission(e);
      return false;
    }
  }

  async requestHighAccuracyLocationPermission(): Promise<boolean> {
    return true;
  }

  private async hasPush(): Promise<boolean> {
    try {
      const result = await navigator.permissions.query({ name: 'push' });
      return result.state === PermissionStateEnum.GRANTED;
    } catch {
      return false;
    }
  }

  private async hasCamera(): Promise<boolean> {
    try {
      const result = await navigator.permissions.query({ name: 'camera' });
      return result.state === PermissionStateEnum.GRANTED;
    } catch {
      return false;
    }
  }

  private async hasLocationPermission(): Promise<LocationPermissions> {
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      const granted = result.state === PermissionStateEnum.GRANTED;
      return {
        inUse: granted,
        gpsEnabled: granted,
        getHighAccuracyPermission: granted,
        background: false,
        canRequestBackground: false,
        canRequestInUse: result.state === PermissionStateEnum.PROMPT,
        canReRequest: false,
      };
    } catch {
      return {
        inUse: false,
        gpsEnabled: false,
        getHighAccuracyPermission: false,
        background: false,
        canRequestBackground: false,
        canRequestInUse: false,
        canReRequest: false,
      };
    }
  }
}
